/**
 * ProgressBanner Component
 * Sticky progress indicator shown while analysis is processing.
 * Displays overall progress bar and cycles through loading texts from the API.
 * The loading text section is collapsible (expanded by default) and overlaps cards below.
 */

import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, ChevronDown, ChevronUp } from 'lucide-react';
import { LoadingProgress } from '@/components/results/LoadingProgress/LoadingProgress';
import { useLoadingTexts } from '@/hooks/useLoadingTexts';

interface ProgressBannerProps {
  progress: number;
  isPolling: boolean;
  productId: number | undefined;
}

/**
 * Manages a two-phase text transition:
 * 1. Old text fades out + slides down
 * 2. New text fades in + slides in from top
 */
function useTextTransition(text: string | null) {
  const [displayText, setDisplayText] = useState(text);
  const [phase, setPhase] = useState<'idle' | 'exit' | 'enter'>('idle');
  const prevTextRef = useRef(text);

  useEffect(() => {
    if (text === prevTextRef.current) return;

    // If there's a previous text, start exit animation first
    if (prevTextRef.current && displayText) {
      setPhase('exit');
      const exitTimer = setTimeout(() => {
        setDisplayText(text);
        setPhase('enter');
        const enterTimer = setTimeout(() => setPhase('idle'), 400);
        return () => clearTimeout(enterTimer);
      }, 300);
      prevTextRef.current = text;
      return () => clearTimeout(exitTimer);
    } else {
      // First text appearing — just enter
      setDisplayText(text);
      setPhase('enter');
      const timer = setTimeout(() => setPhase('idle'), 400);
      prevTextRef.current = text;
      return () => clearTimeout(timer);
    }
  }, [text]);

  const animationClass =
    phase === 'exit'
      ? 'animate-loading-text-exit'
      : phase === 'enter'
        ? 'animate-loading-text-enter'
        : '';

  return { displayText, animationClass };
}

export function ProgressBanner({ progress, isPolling, productId }: ProgressBannerProps) {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(true);
  const { currentText } = useLoadingTexts(productId, isPolling);
  const { displayText, animationClass } = useTextTransition(currentText);

  if (!isPolling) return null;

  const displayProgress = typeof progress === 'number' && !isNaN(progress) ? progress : 0;
  const hasLoadingText = !!displayText;

  return (
    <div className="sticky top-4 z-10 mb-8">
      <div className="bg-white backdrop-blur-md border border-[var(--glass-border)] rounded-[var(--radius-md)] p-4 shadow-sm">
        <div className="flex items-center gap-3 mb-3">
          <Loader2 className="h-5 w-5 animate-spin text-[var(--color-primary-start)]" />
          <div className="flex-1 min-w-0">
            <p className="font-medium text-[var(--color-charcoal)]">
              {t('results.processingBannerTitle')}
            </p>
            <p className="text-sm text-[var(--color-charcoal)]/70">
              {t('results.processingBannerDescription')}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex-1">
            <LoadingProgress progress={displayProgress} />
          </div>
          {hasLoadingText && (
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="shrink-0 p-1 rounded-md hover:bg-gray-100 transition-colors text-[var(--color-charcoal)]/60 hover:text-[var(--color-charcoal)]"
              aria-label={isExpanded ? t('results.collapse') : t('results.expand')}
              aria-expanded={isExpanded}
            >
              {isExpanded ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </button>
          )}
        </div>
        {/* Collapsible loading text section */}
        {hasLoadingText && isExpanded && (
          <div className="mt-3 pt-3 border-t border-[var(--glass-border)] overflow-hidden">
            <p
              className={`text-lg font-bold text-[var(--color-charcoal)]/80 text-center ${animationClass}`}
            >
              {displayText}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
